/**
 * Identidade visual do credor originário no lugar do logo: um círculo com as iniciais e o nome ao
 * lado. As iniciais saem das palavras significativas do nome (`Banco do Brasil` → `BB`), no máximo
 * três. O nome fica numa coluna estreita ao lado do círculo, então o corpo sai do comprimento sem
 * medição em runtime e desce um degrau quando a palavra mais longa não cabe. Nome comprido demais
 * não é exibido: sobra o círculo, que já carrega as iniciais.
 */

export interface IdentidadeCredorOriginario {
  iniciais: string;
  tamanhoFonteIniciais: number;
  /** Ausente quando o nome não cabe na coluna; o círculo responde sozinho pelo originário. */
  nome?: string;
  tamanhoFonteNome: number;
}

/** Corpo em px de cada degrau do nome, do maior para o menor. */
const DEGRAUS_TAMANHO_FONTE = [14, 13, 12, 11];

/** Comprimento de nome atendido por cada degrau de `DEGRAUS_TAMANHO_FONTE`, na mesma ordem. */
const COMPRIMENTOS_MAXIMOS = [10, 16, 24, 31];

const COMPRIMENTO_MAXIMO_NOME = COMPRIMENTOS_MAXIMOS.at(-1) ?? 31;

/** Largura útil da coluna do nome no mobile, onde ela é mais estreita. */
const LARGURA_UTIL_NOME = 104;

/** Avanço médio por caractere do Montserrat 700, usado para prever a quebra sem medir o texto. */
const AVANCO_MEDIO_CARACTERE = 0.62;

const MAXIMO_INICIAIS = 3;

/** Corpo das iniciais por quantidade de caracteres, para o círculo de 40px não ficar apertado. */
const TAMANHO_FONTE_INICIAIS = [16, 14, 12];

/** Ligações que não entram nas iniciais (`Banco do Brasil` → `BB`, não `BDB`). */
const CONECTIVOS = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);

function palavras(nome: string): string[] {
  return nome.split(/\s+/).filter((palavra) => palavra !== '');
}

function palavrasSignificativas(nome: string): string[] {
  const todas = palavras(nome);
  const semConectivos = todas.filter((palavra) => !CONECTIVOS.has(palavra.toLowerCase()));

  return semConectivos.length > 0 ? semConectivos : todas;
}

function comprimentoDaMaiorPalavra(nome: string): number {
  return palavras(nome).reduce((maior, palavra) => Math.max(maior, palavra.length), 0);
}

function cabeNaLinha(comprimento: number, tamanhoFonte: number): boolean {
  return comprimento * tamanhoFonte * AVANCO_MEDIO_CARACTERE <= LARGURA_UTIL_NOME;
}

function tamanhoFonteDoNome(nome: string): number {
  const maiorPalavra = comprimentoDaMaiorPalavra(nome);
  let degrau = COMPRIMENTOS_MAXIMOS.findIndex((maximo) => nome.length <= maximo);

  while (
    degrau < DEGRAUS_TAMANHO_FONTE.length - 1
    && !cabeNaLinha(maiorPalavra, DEGRAUS_TAMANHO_FONTE[degrau])
  ) {
    degrau += 1;
  }

  return DEGRAUS_TAMANHO_FONTE[degrau];
}

/** Iniciais das até três primeiras palavras significativas do nome. */
export function extrairIniciaisCredorOriginario(nome: string): string {
  return palavrasSignificativas(nome)
    .slice(0, MAXIMO_INICIAIS)
    .map((palavra) => palavra.charAt(0).toUpperCase())
    .join('');
}

/**
 * Iniciais do círculo e nome ao lado. Nome com mais de 31 caracteres fica de fora: até 31 a coluna
 * ainda o acomoda nos degraus menores.
 */
export function resolverIdentidadeCredorOriginario(nome: string): IdentidadeCredorOriginario {
  const nomeLimpo = nome.trim();
  const iniciais = extrairIniciaisCredorOriginario(nomeLimpo);
  const tamanhoFonteIniciais = TAMANHO_FONTE_INICIAIS[
    Math.min(Math.max(iniciais.length, 1), MAXIMO_INICIAIS) - 1
  ];

  if (nomeLimpo === '' || nomeLimpo.length > COMPRIMENTO_MAXIMO_NOME) {
    return { iniciais, tamanhoFonteIniciais, tamanhoFonteNome: DEGRAUS_TAMANHO_FONTE[0] };
  }

  const tamanhoFonteNome = tamanhoFonteDoNome(nomeLimpo);

  // O nome nunca quebra no meio da palavra, então palavra que não cabe nem no menor degrau sairia
  // da coluna: nesse caso o círculo responde sozinho pelo originário.
  if (!cabeNaLinha(comprimentoDaMaiorPalavra(nomeLimpo), tamanhoFonteNome)) {
    return { iniciais, tamanhoFonteIniciais, tamanhoFonteNome: DEGRAUS_TAMANHO_FONTE[0] };
  }

  return {
    iniciais,
    tamanhoFonteIniciais,
    nome: nomeLimpo,
    tamanhoFonteNome,
  };
}
